import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PortalShell } from '@/layouts/PortalShell/PortalShell';
import { FooterChrome } from '@/layouts/FooterChrome/FooterChrome';
import { CardGrid } from '@/modules/CardGrid/CardGrid';
import { OmHytta } from '@/modules/OmHytta/OmHytta';
import { Eierseksjon } from '@/modules/Eierseksjon/Eierseksjon';
import { Hytteregler } from '@/modules/Hytteregler/Hytteregler';
import { Utforsk } from '@/modules/Utforsk/Utforsk';
import { Vedlikehold } from '@/modules/Vedlikehold/Vedlikehold';
import { Galleri } from '@/modules/Galleri/Galleri';
import { Gjestebok } from '@/modules/Gjestebok/Gjestebok';
import { Faq } from '@/modules/Faq/Faq';
import { MODULES, type ModuleId } from '@/modules/registry';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { playLandingEntry, showLandingAtRest } from '@/animations/landingEntry';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useSession } from '@/services/auth';
import { nb } from '@/i18n/nb';
import bgImg from '@/assets/bg-img-1.jpg?w=640;1024;1600;2400&format=webp&as=picture';
import wordmark from '@/assets/Logo_1_trim.png';
import s from './Landing.module.css';

export function Landing() {
  const heroRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();
  const { isLoading: sessionLoading } = useSession();
  const [activeId, setActiveId] = useState<ModuleId | null>(null);
  // Single-fire guard for the entry animation. Refs are reactive-free, so the
  // useEffect below can include sessionLoading without re-animating on later
  // session-state churn (logout, login redirects).
  const hasAnimatedRef = useRef(false);

  const activeModule = activeId ? MODULES.find((m) => m.id === activeId) : null;

  // Pre-hide cards before paint so the gsap timeline has a clean canvas to
  // animate from. Runs on mount AND after the session probe resolves — that
  // second pass is the important one: owner/admin gets Eierseksjon as the 8th
  // card *only after* `useSession` resolves, and without pre-hiding it lands
  // visible-at-rest while the other seven animate around it (GATE-2 defect 2).
  // useLayoutEffect runs before paint, so the user never sees the un-hidden
  // intermediate state.
  useLayoutEffect(() => {
    if (hasAnimatedRef.current || !canvasRef.current) return;
    canvasRef.current.querySelectorAll<HTMLButtonElement>('button').forEach((card) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(32px)';
    });
  }, [sessionLoading]);

  useEffect(() => {
    // Wait until the session probe resolves before running the entry timeline —
    // CardGrid filters owner-only cards on the session result, so a timeline
    // captured pre-resolution would miss Eierseksjon for owner/admin. The probe
    // is fast (~50–200 ms) and the pre-hide useLayoutEffect above keeps the
    // cards invisible across the gap.
    if (sessionLoading || hasAnimatedRef.current) return;
    hasAnimatedRef.current = true;
    const hero = heroRef.current ? Array.from(heroRef.current.children) : [];
    const cards = canvasRef.current ? Array.from(canvasRef.current.querySelectorAll('button')) : [];
    const tl = playLandingEntry({
      hero: hero as HTMLElement[],
      cards: cards as HTMLElement[],
      reducedMotion,
    });
    return () => {
      tl?.kill();
      // Restore the resting state so StrictMode's dev double-invoke (which kills the timeline
      // between its two effect passes, while the guard blocks the re-run) can't strand the
      // pre-hidden cards at opacity 0 — a remount-only bug, e.g. admin → "Tilbake til portalen".
      showLandingAtRest([...hero, ...cards]);
    };
    // reducedMotion captured at the single fire; later changes don't re-animate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoading]);

  return (
    <PortalShell>
      <div className={s.photo}>
        <picture>
          {Object.entries(bgImg.sources).map(([type, srcset]) => (
            <source key={type} type={`image/${type}`} srcSet={srcset} sizes="100vw" />
          ))}
          <img
            className={s.photoImg}
            src={bgImg.img.src}
            width={bgImg.img.w}
            height={bgImg.img.h}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
          />
        </picture>
        <div className={s.veil} aria-hidden="true" />
        <div className={s.hero} ref={heroRef}>
          <h1 className={s.wordmark}>
            <img src={wordmark} alt={nb.landing.wordmarkAlt} />
          </h1>
          <p className={s.tagline}>{nb.landing.tagline}</p>
        </div>
        <div className={s.waves} aria-hidden="true">
          <svg viewBox="0 0 1200 46" preserveAspectRatio="none" fill="none">
            <path
              d="M0 32 C 120 16, 220 16, 340 30 S 620 46, 760 30 S 1020 14, 1200 28 L1200 46 L0 46 Z"
              fill="var(--color-sea-foam)"
              opacity="0.92"
            />
            <path
              d="M0 36 C 140 22, 250 22, 380 35 S 660 49, 800 35 S 1060 20, 1200 33"
              stroke="var(--color-sea-light)"
              strokeWidth="2"
              opacity="0.8"
            />
            <path
              d="M0 26 C 110 12, 210 12, 330 24 S 600 38, 740 24 S 1010 10, 1200 22"
              stroke="var(--color-paper-raised)"
              strokeWidth="1.6"
              opacity="0.75"
            />
          </svg>
        </div>
      </div>

      <div className={s.canvas} ref={canvasRef}>
        <CardGrid onOpen={setActiveId} />
      </div>

      <FooterChrome />

      {activeModule &&
        (activeModule.id === 'regler' ? (
          // Hytteregler owns its own Overlay (nested sub-card navigation).
          <Hytteregler onClose={() => setActiveId(null)} />
        ) : activeModule.id === 'vedlikehold' ? (
          // Vedlikehold also owns its own Overlay (nested two-sub-card pattern,
          // ADR-0028). Same structure as Hytteregler — Generelt + Dugnadsoppgaver.
          <Vedlikehold onClose={() => setActiveId(null)} />
        ) : activeModule.id === 'utforsk' ? (
          // Utforsk is a full-viewport map — no slide-overlay, no footer chrome
          // (design-spec §8 + Iter-6 wireframe). It manages its own dismiss button.
          <Utforsk onClose={() => setActiveId(null)} />
        ) : activeModule.id === 'galleri' ? (
          // Galleri owns its own Overlay (grid + viewer + Rediger-modus upload).
          <Galleri onClose={() => setActiveId(null)} />
        ) : (
          <Overlay title={activeModule.title} onClose={() => setActiveId(null)}>
            {activeModule.id === 'om-hytta' ? (
              <OmHytta />
            ) : activeModule.id === 'gjestebok' ? (
              <Gjestebok />
            ) : activeModule.id === 'eier' ? (
              <Eierseksjon />
            ) : activeModule.id === 'faq' ? (
              <Faq />
            ) : (
              <p className={s.comingSoon}>{nb.modules.comingSoonBody}</p>
            )}
          </Overlay>
        ))}
    </PortalShell>
  );
}
